import { ChangeDetectorRef, Component, ElementRef, Input, ViewChild
       } from '@angular/core';

import { Utils } from 'app/core/utils';

type TweenEquation =
  (time: number, begin: number, change: number, duration: number) => number;

interface TweenTable {
  [name: string]: TweenEquation;
}

const tweenEquations: TweenTable = {
  // credit to http://www.gizma.com/easing/

  'linear': (t: number, b: number, c: number, d: number) =>
    c * t / d + b,

  'sine-out': (t: number, b: number, c: number, d: number) =>
    c * Math.sin(t / d * (Math.PI / 2)) + b,

  'quad-out': (t: number, b: number, c: number, d: number) => {
    const tt = t / d;
    return -c * tt * (tt - 2) + b;
  },

  'exp-out': (t: number, b: number, c: number, d: number) =>
    c * (-Math.pow(2, -10 * t / d ) + 1) + b,

  'circ-out': (t: number, b: number, c: number, d: number) => {
    const tt = (t / d) - 1;
    return c * Math.sqrt(1 - tt * tt) + b;
  },
};

function makeChunkedInterval(
    start: number, end: number, chunks: number): (position: number) => number
{
  const step = (end - start) / chunks;
  const noise = () => Math.random() * (step / 2);

  const divisions: Array<number> = [];

  divisions.push(start);

  for (let i = 0; i < chunks; ++i) {
    divisions.push(Math.min(i * step + noise(), end));
  }

  divisions.push(end);

  return (position: number) =>
    Utils.find(divisions, (x: number) => x >= position);
}

class Progressor
{
  private static readonly RESOLUTION_MS: number = 100;
  private static readonly FINISH_DELAY: number = 200;
  private timer: any = undefined;

  constructor(
    durationMs: number,
    private onProgress: (percent: number) => void,
    private onOverTime: () => void)
  {
    const steps = durationMs / Progressor.RESOLUTION_MS;
    const minChunks = steps * (1 / 4);
    const maxChunks = steps * (3 / 4);

    const numChunks = Math.floor(
      minChunks + Math.random() * (maxChunks - minChunks));

    const tweenEqs = Utils.byValue(tweenEquations);
    const tweenIndex = Math.floor(Math.random() * tweenEqs.length);
    const tweening = tweenEqs[tweenIndex];

    const interval = makeChunkedInterval(0, durationMs, numChunks);
    const progress = (time: number) => tweening(time, 0, 100, durationMs);

    const advance = (time: number) => {
      this.onProgress(progress(interval(time)));

      if (time < durationMs) {
        this.timer = setTimeout(
          () => advance(time + Progressor.RESOLUTION_MS),
          Progressor.RESOLUTION_MS);
      } else {
        this.timer = undefined;
        this.onOverTime();
      }
    };

    advance(0);
  }

  public detach(): void
  {
    this.onProgress = (_: number) => {};
    this.onOverTime = () => {};
  }

  public cancel(onCanceled: () => void): void
  {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }

    this.onProgress(100);
    setTimeout(() => {
      onCanceled();
    }, Progressor.FINISH_DELAY);
  }
}

export enum ProgressType
{
  Spinner,
  Progress,
  FinalProgress,
}

@Component({
    selector: 'progress-overlay',
    templateUrl: './progress-overlay.component.html',
    styleUrls: ['./progress-overlay.component.scss'],
    standalone: false
})
export class ProgressOverlayComponent
{
  private static readonly FAKE_PROGRESS_DURATION: number = 40000; /* ms */

  @Input('visible') public visible: boolean = false;
  @ViewChild('spinnerContent') public spinnerContent: ElementRef;

  public progressTypeEnum: any = ProgressType;

  public progress: number = 0;
  public progressType: ProgressType = ProgressType.Spinner;
  public mode: string = 'short';
  private progressor: Progressor | undefined = undefined;
  private count: number = 0;

  constructor(
      private cdr: ChangeDetectorRef)
  { }

  public startProgress(progressType: ProgressType | undefined): void
  {
    this.updateProgressType(progressType);
    this.visible = true;

    if ([ProgressType.Progress, ProgressType.FinalProgress]
        .includes(progressType)) {
      this.startFakeProgress();
    }

    this.count += 1;
  }

  private updateProgressType(progressType: ProgressType | undefined): void
  {
    // We only update the progressType in specific places to ensure that the
    // progress bar does not visually change suddenly.
    if (progressType === undefined) {
      return;
    }

    this.progressType = progressType;
    if ([ProgressType.Progress, ProgressType.FinalProgress]
        .includes(progressType)) {
      this.mode = 'determinate';
    }
    else {
      this.mode = 'short';
    }
  }

  public finishProgress(progressType: ProgressType | undefined): void
  {
    if (this.count > 0) {
      this.count--;
    }

    const onFinished = () => {
      if (this.count <= 0) {
        if (progressType !== ProgressType.FinalProgress) {
          this.visible = false;
        }
        this.updateProgressType(progressType);
      }
    };

    if (this.count <= 0) {
      if ([ProgressType.Progress, ProgressType.FinalProgress].includes(
            this.progressType)) {
        this.finishFakeProgress(onFinished);
      }
      else {
        onFinished();
      }
    }
  }

  public cancelProgress(progressType: ProgressType | undefined,
                        force: boolean = false): void
  {
    if (force) {
      this.count = 0;
    }
    if (this.count > 0) {
      this.count--;
    }

    const onCanceled = () => {
      if (this.count <= 0) {
        this.visible = false;
        this.updateProgressType(progressType);
      }
    };

    if (this.count <= 0) {
      if ([ProgressType.Progress, ProgressType.FinalProgress].includes(
            this.progressType)) {
        this.cancelFakeProgress(onCanceled);
      }
      else {
        onCanceled();
      }
    }
  }

  private startFakeProgress(): void
  {
    this.progress = 0;
    this.cdr.detectChanges();

    let y = Math.round((window.innerHeight / 2)
        - this.spinnerContent.nativeElement.offsetHeight);
    y = y > 0 ? y : 0;
    Utils.scrollToPosition(0, y);

    if (this.progressor) {
      this.progressor.detach();
    }

    this.progressor = new Progressor(
      ProgressOverlayComponent.FAKE_PROGRESS_DURATION,
      p => this.progress = p,
      () => this.mode = 'indeterminate');
  }

  private finishFakeProgress(onFinished: () => void): void
  {
    this.cancelFakeProgress(() => {
      this.progress = 100;

      onFinished();
    });
  }

  public cancelFakeProgress(onCanceled: () => void): void
  {
    if (!this.progressor) {
      onCanceled();
      return;
    }

    this.progressor.cancel(onCanceled);
  }
}
